import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { lectureAPI, questionsAPI } from '../lib/api';
import { ImageZoom } from '../components/ImageZoom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { HighlightableText } from '../components/HighlightableText';
import { ExplanationRenderer } from '../components/core/ExplanationRenderer';
import { cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import {
    Loader2,
    CheckCircle,
    XCircle,
    ArrowRight,
    Clock,
    Lightbulb,
    Trophy,
    ArrowLeft,
    FileText
} from 'lucide-react';
import Layout from '../components/Layout';

export default function LecturePractice() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { isDark } = useTheme();
    
    const [loading, setLoading] = useState(true);
    const [question, setQuestion] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [lockedAnswer, setLockedAnswer] = useState(null);
    const [result, setResult] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [sessionComplete, setSessionComplete] = useState(false);
    const [finalStats, setFinalStats] = useState(null);
    const [strikethrough, setStrikethrough] = useState([]);
    const [startTime, setStartTime] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [loadingImage, setLoadingImage] = useState(false);

    useEffect(() => {
        loadQuestion();
    }, [sessionId]);

    // Poll for background-generated image
    useEffect(() => {
        if (!question) return;
        if (question.image_base64) return;
        if (!question.image_description) return;
        if (question.image_ready) return;

        setLoadingImage(true);
        let cancelled = false;
        const poll = async () => {
            for (let i = 0; i < 20; i++) {
                if (cancelled) return;
                await new Promise(r => setTimeout(r, 2000));
                if (cancelled) return;
                try {
                    const res = await questionsAPI.getImage(question.id);
                    if (res.data.status === 'ready' && res.data.image_base64) {
                        if (!cancelled) {
                            setQuestion(prev => prev?.id === question.id ? { ...prev, image_base64: res.data.image_base64, image_ready: true } : prev);
                            setLoadingImage(false);
                        }
                        return;
                    }
                    if (res.data.status === 'no_image') {
                        setLoadingImage(false);
                        return;
                    }
                } catch { /* continue polling */ }
            }
            if (!cancelled) setLoadingImage(false);
        };
        poll();
        return () => { cancelled = true; };
    }, [question?.id, question?.image_base64, question?.image_description, question?.image_ready]);

    // Timer
    useEffect(() => {
        let interval;
        if (startTime && !submitted && question) {
            interval = setInterval(() => {
                setElapsed(Math.floor((Date.now() - startTime) / 1000));
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [startTime, submitted, question]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const loadQuestion = async () => {
        setLoading(true);
        let lastError = null;
        for (let attempt = 0; attempt < 4; attempt++) {
            try {
                let data;
                if (attempt < 2) {
                    const response = await lectureAPI.getSessionQuestion(sessionId);
                    data = response.data;
                } else {
                    const token = localStorage.getItem('medprep_token');
                    const res = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/lecture-sessions/${sessionId}/question`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                    );
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    data = await res.json();
                }

                if (data.status === 'completed') {
                    setSessionComplete(true);
                    const results = data.results;
                    setFinalStats({ correct: results?.correct ?? 0, total: results?.total ?? 0 });
                } else {
                    setQuestion(data.question);
                    setProgress({ current: data.progress?.current ?? 0, total: data.progress?.total ?? 0 });
                    setSelectedAnswer(null);
                    setSubmitted(false);
                    setSubmitting(false);
                    setLockedAnswer(null);
                    setResult(null);
                    setStrikethrough([]);
                    setStartTime(Date.now());
                    setElapsed(0);
                    setLoadingImage(false);
                }
                setLoading(false);
                return; // Success
            } catch (err) {
                lastError = err;
                if (attempt < 3) await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
            }
        }
        console.error('Failed to load question after 4 attempts:', lastError);
        toast.error('Connection issue loading question. Please try again.');
        setLoading(false);
    };

    const handleSubmit = async () => {
        if (!selectedAnswer || !question) return;
        
        const answeredWith = lockedAnswer || selectedAnswer;
        setLockedAnswer(answeredWith);
        setSubmitting(true);

        // Aggressive retry: try up to 4 times with short delays
        // Uses fetch as fallback if axios fails (different network path)
        let lastError = null;
        for (let attempt = 0; attempt < 4; attempt++) {
            try {
                let data;
                if (attempt < 2) {
                    // First 2 attempts: use axios
                    const response = await lectureAPI.submitAnswer(sessionId, question.id, answeredWith);
                    data = response.data;
                } else {
                    // Last 2 attempts: use native fetch as fallback
                    const token = localStorage.getItem('medprep_token');
                    const res = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/lecture-sessions/${sessionId}/answer`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify({ question_id: question.id, answer: answeredWith }),
                        }
                    );
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    data = await res.json();
                }

                // Success
                setResult(data);
                setSubmitted(true);
                if (data.is_correct) toast.success('Correct!');

                // Poll for enriched explanation in background
                const explanation = data.explanation || '';
                if (explanation.length < 300 && !explanation.toLowerCase().includes('why the other')) {
                    setTimeout(async () => {
                        try {
                            const enriched = await lectureAPI.getExplanation(question.id);
                            if (enriched.data.explanation && enriched.data.explanation.length > explanation.length) {
                                setResult(prev => prev ? { ...prev, explanation: enriched.data.explanation } : prev);
                            }
                        } catch { /* ignore */ }
                    }, 6000);
                }
                return; // Success — exit the retry loop
            } catch (err) {
                lastError = err;
                if (attempt < 3) await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
            }
        }

        // All 4 attempts failed
        console.error('Failed to submit answer after 4 attempts:', lastError);
        toast.error('Connection issue — please click Submit to try again.');
        setSubmitting(false);
    };

    const handleNext = () => {
        loadQuestion();
    };

    const toggleStrikethrough = (letter) => {
        if (submitted) return;
        setStrikethrough(prev => 
            prev.includes(letter) 
                ? prev.filter(l => l !== letter)
                : [...prev, letter]
        );
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
            </Layout>
        );
    }

    // Session complete view
    if (sessionComplete && finalStats) {
        const accuracy = finalStats.total > 0 
            ? Math.round((finalStats.correct / finalStats.total) * 100) 
            : 0;

        return (
            <Layout>
                <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="session-complete">
                    <Card className={cn(
                        "border-2",
                        isDark ? "bg-slate-800 border-slate-700" : "border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50"
                    )}>
                        <CardContent className="p-8 text-center">
                            <div className={cn(
                                "w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center",
                                isDark ? "bg-emerald-900/50" : "bg-emerald-100"
                            )}>
                                <Trophy className={cn("h-10 w-10", isDark ? "text-emerald-400" : "text-emerald-600")} />
                            </div>
                            <h2 className={cn(
                                "text-2xl font-bold mb-2",
                                isDark ? "text-white" : "text-slate-900"
                            )} style={{ fontFamily: 'Manrope' }}>
                                Practice Complete!
                            </h2>
                            <p className={cn("mb-6", isDark ? "text-slate-300" : "text-slate-600")}>
                                You've completed your lecture slide practice session
                            </p>
                            
                            <div className={cn(
                                "rounded-lg p-6 mb-6",
                                isDark ? "bg-slate-900/50" : "bg-white"
                            )}>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-slate-900")}>
                                            {finalStats.correct}
                                        </p>
                                        <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Correct</p>
                                    </div>
                                    <div>
                                        <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-slate-900")}>
                                            {finalStats.total}
                                        </p>
                                        <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Total</p>
                                    </div>
                                    <div>
                                        <p className={cn(
                                            "text-3xl font-bold",
                                            accuracy >= 70 ? "text-emerald-600" : accuracy >= 50 ? "text-amber-600" : "text-red-600"
                                        )}>
                                            {accuracy}%
                                        </p>
                                        <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Accuracy</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 justify-center">
                                <Button variant="outline" onClick={() => navigate('/study-planner')} data-testid="back-btn">
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back to Study Planner
                                </Button>
                                <Button onClick={() => navigate('/dashboard')} data-testid="dashboard-btn">
                                    View Dashboard
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="lecture-practice">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Badge className="bg-indigo-100 text-indigo-700">
                            <FileText className="h-4 w-4 mr-1" />
                            Lecture Practice
                        </Badge>
                        <Badge variant="outline">
                            Question {progress.current} of {progress.total}
                        </Badge>
                        {question?.difficulty && (
                            <Badge className={cn(
                                question.difficulty === 'easy' && "bg-emerald-100 text-emerald-700",
                                question.difficulty === 'medium' && "bg-amber-100 text-amber-700",
                                question.difficulty === 'hard' && "bg-red-100 text-red-700"
                            )}>
                                {question.difficulty.charAt(0).toUpperCase() + question.difficulty.slice(1)}
                            </Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-4">
                        <div className={cn(
                            "flex items-center gap-2",
                            isDark ? "text-slate-300" : "text-slate-500"
                        )}>
                            <Clock className="h-4 w-4" />
                            <span className="font-mono">{formatTime(elapsed)}</span>
                        </div>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => navigate('/study-planner')}
                            className="text-slate-500"
                        >
                            Exit
                        </Button>
                    </div>
                </div>

                {/* Progress Bar */}
                <Progress value={(progress.current / progress.total) * 100} className="h-2" />

                {/* Vignette */}
                {question?.vignette && (
                    <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "border-slate-200 bg-slate-50")}>
                        <CardContent className="p-6">
                            {/* Medical Image */}
                            {question.image_base64 && (
                                <div className="mb-4">
                                    <div className="relative rounded-lg overflow-hidden border-2 border-slate-300 bg-white animate-in fade-in duration-500">
                                        <ImageZoom 
                                            src={`data:image/png;base64,${question.image_base64}`}
                                            alt="Medical image for examination"
                                        />
                                    </div>
                                    {submitted && question.image_description && (
                                        <div className={cn(
                                            "mt-3 p-3 rounded-lg border",
                                            isDark ? "bg-indigo-900/30 border-indigo-700" : "bg-indigo-50 border-indigo-200"
                                        )}>
                                            <p className={cn("text-sm font-medium mb-1", isDark ? "text-indigo-300" : "text-indigo-800")}>Image Description:</p>
                                            <p className={cn("text-sm", isDark ? "text-indigo-200" : "text-indigo-700")}>
                                                {question.image_description}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Image loading placeholder */}
                            {!question.image_base64 && loadingImage && (
                                <div className="mb-4">
                                    <div className={cn(
                                        "relative rounded-lg overflow-hidden border-2 h-48 flex items-center justify-center",
                                        isDark ? "border-slate-600 bg-slate-700" : "border-slate-200 bg-slate-100"
                                    )}>
                                        <div className="flex flex-col items-center gap-2 text-slate-400">
                                            <Loader2 className="h-6 w-6 animate-spin" />
                                            <span className="text-sm">Loading medical image...</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <HighlightableText 
                                text={question.vignette} 
                                className={cn("leading-relaxed", isDark ? "text-slate-200" : "text-slate-700")}
                            />
                        </CardContent>
                    </Card>
                )}

                {/* Question */}
                <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "border-slate-200")}>
                    <CardContent className="p-6">
                        <p className={cn(
                            "text-lg font-medium mb-6",
                            isDark ? "text-white" : "text-slate-900"
                        )}>
                            {question?.question_stem}
                        </p>

                        {/* Options */}
                        <div className="space-y-3">
                            {question?.options?.map((option) => {
                                const answered = lockedAnswer || selectedAnswer;
                                const isLocked = submitted || submitting;
                                const isSelected = answered === option.letter;
                                const isCorrect = submitted && option.letter === result?.correct_answer;
                                const isWrong = submitted && isSelected && !isCorrect;
                                const isStruckThrough = strikethrough.includes(option.letter);

                                return (
                                    <div
                                        key={option.letter}
                                        onClick={() => !isLocked && setSelectedAnswer(option.letter)}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            toggleStrikethrough(option.letter);
                                        }}
                                        className={cn(
                                            "flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                                            isDark ? "border-slate-600" : "border-slate-200",
                                            !isLocked && !isDark && "hover:border-indigo-300 hover:bg-indigo-50/50",
                                            !isLocked && isDark && "hover:border-indigo-500 hover:bg-indigo-900/30",
                                            isSelected && !submitted && !isDark && "border-indigo-500 bg-indigo-50",
                                            isSelected && !submitted && isDark && "border-indigo-500 bg-indigo-900/40",
                                            isCorrect && "border-emerald-500 bg-emerald-100",
                                            isWrong && "border-red-500 bg-red-100",
                                            isStruckThrough && !isLocked && "opacity-50",
                                            isLocked && !isSelected && !isCorrect && "opacity-60"
                                        )}
                                        data-testid={`option-${option.letter}`}
                                    >
                                        <div className={cn(
                                            "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm",
                                            !submitted && isSelected && "bg-indigo-600 text-white",
                                            !submitted && !isSelected && (isDark ? "bg-slate-700 text-slate-300" : "bg-slate-200 text-slate-600"),
                                            isCorrect && "bg-emerald-600 text-white",
                                            isWrong && "bg-red-600 text-white"
                                        )}>
                                            {submitted && isCorrect && <CheckCircle className="h-5 w-5" />}
                                            {submitted && isWrong && <XCircle className="h-5 w-5" />}
                                            {!submitted && option.letter}
                                            {submitted && !isCorrect && !isWrong && option.letter}
                                        </div>
                                        <span className={cn(
                                            "flex-1",
                                            isStruckThrough && !isLocked && "line-through text-slate-400",
                                            isCorrect && "text-emerald-900 font-medium",
                                            isWrong && "text-red-900",
                                            !isCorrect && !isWrong && isDark && "text-slate-200"
                                        )}>
                                            {option.text}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {!submitted && (
                            <p className={cn(
                                "mt-4 text-sm flex items-center gap-2",
                                isDark ? "text-slate-400" : "text-slate-500"
                            )}>
                                <Lightbulb className="h-4 w-4" />
                                Tip: Right-click an option to strike through it
                            </p>
                        )}
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                    {!submitted ? (
                        <Button 
                            onClick={handleSubmit}
                            disabled={!selectedAnswer || submitting}
                            size="lg"
                            data-testid="submit-btn"
                        >
                            {submitting ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Checking...</>
                            ) : (
                                <>Submit Answer<ArrowRight className="ml-2 h-4 w-4" /></>
                            )}
                        </Button>
                    ) : (
                        <Button 
                            onClick={handleNext}
                            size="lg"
                            data-testid="next-btn"
                        >
                            Next Question
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    )}
                </div>

                {/* Explanation */}
                {submitted && result && (
                    <Card className={cn(
                        "border-2",
                        result.is_correct 
                            ? "border-emerald-500 bg-emerald-100" 
                            : "border-amber-500 bg-amber-100"
                    )}>
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
                                {result.is_correct ? (
                                    <>
                                        <CheckCircle className="h-5 w-5 text-emerald-600" />
                                        <span className="text-emerald-800">Correct!</span>
                                    </>
                                ) : (
                                    <>
                                        <Lightbulb className="h-5 w-5 text-amber-600" />
                                        <span className="text-amber-800">Explanation</span>
                                    </>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <ExplanationRenderer
                                text={result.explanation || "The correct answer demonstrates the key concept being tested."}
                                className={cn(
                                    "text-sm leading-relaxed",
                                    result.is_correct ? "text-emerald-900" : "text-amber-900"
                                )}
                            />
                            {result.concept && (
                                <p className={cn(
                                    "mt-3 text-sm font-medium",
                                    result.is_correct ? "text-emerald-800" : "text-amber-800"
                                )}>
                                    Key Concept: {result.concept}
                                </p>
                            )}
                            {!result.is_correct && (
                                <p className="mt-3 text-sm text-amber-800">
                                    The correct answer was <strong>{result.correct_answer}</strong>.
                                </p>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}
